import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@roger/ui-components';
import { ShieldCheck } from 'lucide-react';
import type { SigningAttestation } from './attestations';

interface AttestationDialogProps {
  open: boolean;
  /** When null (e.g. still loading from API), body shows a loading state. */
  attestation: SigningAttestation | null;
  signerName: string | null;
  signerCredentials?: string | null;
  isLoading?: boolean;
  actionLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Pre-sign confirmation dialog.
 *
 * The signer must read and acknowledge the attestation text before the actual
 * `/sign` call fires. The client sends `attestation_version` for server validation.
 */
export const AttestationDialog: React.FC<AttestationDialogProps> = ({
  open,
  attestation,
  signerName,
  signerCredentials,
  isLoading = false,
  actionLabel = 'Sign',
  onConfirm,
  onCancel,
}) => {
  const displayName = [signerName, signerCredentials]
    .filter(Boolean)
    .join(', ');
  return (
    <Dialog
      open={open}
      onOpenChange={next => !next && !isLoading && onCancel()}
    >
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <ShieldCheck className='h-5 w-5 text-primary' />
            Confirm Electronic Signature
          </DialogTitle>
          <DialogDescription className='space-y-3 pt-2 text-sm'>
            <div className='rounded-md border border-border/70 bg-muted/30 px-3 py-3 text-foreground'>
              {attestation ? (
                <p className='whitespace-pre-wrap leading-relaxed'>
                  {attestation.text}
                </p>
              ) : (
                <p className='text-muted-foreground text-sm'>
                  Loading attestation…
                </p>
              )}
            </div>
            {displayName ? (
              <div className='text-muted-foreground'>
                Signing as{' '}
                <span className='font-medium text-foreground'>
                  {displayName}
                </span>
              </div>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isLoading || !attestation}>
            {isLoading ? 'Signing…' : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
